/* MABLE free shipping pop-up — see sections/mable-popup.liquid
 * Extends the theme's Modal (assets/modal.js). Submits the newsletter form to
 * Shopify's native /contact endpoint via fetch, then shows the discount code.
 */
/* global Modal */

(() => {
  const DAY = 24 * 60 * 60 * 1000;

  const store = {
    get(key) {
      try { return localStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value, days) {
      try {
        localStorage.setItem(key, JSON.stringify({ v: value, exp: Date.now() + days * DAY }));
      } catch (e) { /* storage unavailable */ }
    },
    has(key) {
      const raw = store.get(key);
      if (!raw) return false;
      try {
        const data = JSON.parse(raw);
        if (data.exp && data.exp < Date.now()) { localStorage.removeItem(key); return false; }
        return true;
      } catch (e) { return false; }
    },
    remove(key) {
      try { localStorage.removeItem(key); } catch (e) { /* noop */ }
    }
  };

  function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    return new Promise((resolve) => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* noop */ }
      ta.remove();
      resolve();
    });
  }

  function bindCopyButtons(root) {
    root.querySelectorAll('[data-copy]').forEach((btn) => {
      btn.addEventListener('click', () => {
        copyToClipboard(btn.dataset.copy).then(() => {
          btn.setAttribute('data-copied', '');
          setTimeout(() => btn.removeAttribute('data-copied'), 2000);
        });
      });
    });
  }

  if (!customElements.get('mable-popup')) {
    customElements.whenDefined('modal-dialog').then(() => {
      class MablePopup extends Modal {
        constructor() {
          super();
          const key = 'mable-popup';
          this.keys = {
            dismissed: `${key}-dismissed`,
            subscribed: `${key}-subscribed`,
            tabClosed: `${key}-tab-closed`,
            barClosed: `${key}-bar-closed`,
            pending: `${key}-pending`
          };

          this.form = this.querySelector('form');
          this.states = {
            start: this.querySelector('[data-state="start"]'),
            success: this.querySelector('[data-state="success"]')
          };
          this.errorEl = this.querySelector('[data-error]');
          this.tab = document.querySelector(`.mable-popup-tab[data-popup="${this.id}"]`);
          this.bar = document.querySelector(`.mable-popup-bar[data-popup="${this.id}"]`);

          bindCopyButtons(this);
          if (this.bar) bindCopyButtons(this.bar);

          if (this.form) {
            this.form.addEventListener('submit', this.handleSubmit.bind(this));
            const btn = this.form.querySelector('button.btn');
            if (btn && this.dataset.buttonLabel) btn.textContent = this.dataset.buttonLabel;
          }

          if (this.tab) {
            this.tab.querySelector('[data-open-popup]').addEventListener('click', () => this.open());
            this.tab.querySelector('[data-close-tab]').addEventListener('click', () => {
              this.hideTab();
              store.set(this.keys.tabClosed, true, Number(this.dataset.dismissDays));
            });
          }

          if (this.bar) {
            this.bar.querySelector('[data-close-bar]').addEventListener('click', () => {
              this.hideBar();
              store.set(this.keys.barClosed, true, 400);
            });
          }

          if (Shopify.designMode) {
            document.addEventListener('shopify:section:select', (evt) => {
              if (evt.target === this.closest('.shopify-section')) this.open();
            });
            document.addEventListener('shopify:section:deselect', () => super.close());
            return;
          }

          this.init();
        }

        init() {
          const mobileOk = this.dataset.showOnMobile === 'true' || (window.theme && theme.mediaMatches && theme.mediaMatches.md);
          const url = new URL(window.location.href);

          // Subscribed through ANY newsletter form on the site (footer, subscribe page...):
          // treat them as subscribed and never nag.
          if (url.searchParams.get('customer_posted') === 'true' && !store.has(this.keys.pending)) {
            store.set(this.keys.subscribed, true, 400);
          }

          // Arrived with the discount already applied (e.g. /discount/FREEPOST from an EDM).
          if (/(^|;\s*)discount_code=/.test(document.cookie) && !store.has(this.keys.subscribed)) {
            store.set(this.keys.subscribed, true, 400);
          }

          // Returning from a native (non-fetch) submit, e.g. after a captcha challenge.
          if (url.searchParams.get('customer_posted') === 'true' && store.has(this.keys.pending)) {
            store.remove(this.keys.pending);
            this.markSubscribed();
            this.showSuccess();
            this.open();
            url.searchParams.delete('customer_posted');
            url.hash = '';
            window.history.replaceState({}, '', url.toString());
            window.scrollTo(0, 0);
            return;
          }

          if (store.has(this.keys.subscribed)) {
            if (!store.has(this.keys.barClosed)) this.showBar();
            return;
          }

          if (store.has(this.keys.dismissed)) {
            if (!store.has(this.keys.tabClosed)) this.showTab();
            return;
          }

          if (!mobileOk) return;
          this.arm();
        }

        /**
         * Sets up the engagement trigger. Opens once when the rule is met:
         *   engaged: (page views >= minPages OR seconds on page >= minSeconds) AND scrolled >= minScroll
         *   delay:   seconds on page >= minSeconds
         * Exit intent (desktop) can open it regardless.
         */
        arm() {
          const d = this.dataset;
          const sessionKey = 'mable-popup-session';
          let session = { pages: 0, shown: false };
          try { session = JSON.parse(sessionStorage.getItem(sessionKey)) || session; } catch (e) { /* noop */ }
          session.pages += 1;
          try { sessionStorage.setItem(sessionKey, JSON.stringify(session)); } catch (e) { /* noop */ }

          if (d.oncePerSession === 'true' && session.shown) return;

          const minPages = Number(d.minPages) || 1;
          const minSeconds = Number(d.minSeconds) || 0;
          const minScroll = Number(d.minScroll) || 0;
          let timeMet = minSeconds === 0;
          let scrollMet = minScroll === 0;
          const pagesMet = session.pages >= minPages;
          let fired = false;

          const fire = () => {
            if (fired) return;
            if (store.has(this.keys.dismissed) || store.has(this.keys.subscribed)) return;
            fired = true;
            session.shown = true;
            try { sessionStorage.setItem(sessionKey, JSON.stringify(session)); } catch (e) { /* noop */ }
            window.removeEventListener('scroll', onScroll);
            document.body.removeEventListener('mouseleave', onLeave);
            this.open();
          };

          const check = () => {
            if (d.trigger === 'delay') {
              if (timeMet) fire();
            } else if ((pagesMet || timeMet) && scrollMet) {
              fire();
            }
          };

          const onScroll = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const pct = max > 0 ? (window.scrollY / max) * 100 : 100;
            if (pct >= minScroll) {
              scrollMet = true;
              window.removeEventListener('scroll', onScroll);
              check();
            }
          };

          const onLeave = (evt) => { if (evt.clientY < 0) fire(); };

          if (!scrollMet) window.addEventListener('scroll', onScroll, { passive: true });
          if (!timeMet) setTimeout(() => { timeMet = true; check(); }, minSeconds * 1000);
          if (d.exitIntent === 'true' && window.theme && theme.mediaMatches && theme.mediaMatches.md) {
            document.body.addEventListener('mouseleave', onLeave);
          }
          check();
        }

        close() {
          super.close();
          if (!store.has(this.keys.subscribed)) {
            store.set(this.keys.dismissed, true, Number(this.dataset.dismissDays));
            if (!store.has(this.keys.tabClosed)) this.showTab();
          }
        }

        showTab() { if (this.tab) this.tab.hidden = false; }
        hideTab() { if (this.tab) this.tab.hidden = true; }

        showBar() {
          if (!this.bar) return;
          // Render at the very top of the page rather than inside the overlay group.
          if (this.bar.parentElement !== document.body || document.body.firstElementChild !== this.bar) {
            document.body.prepend(this.bar);
          }
          this.bar.hidden = false;
        }
        hideBar() { if (this.bar) this.bar.hidden = true; }

        showSuccess() {
          if (this.states.start) this.states.start.hidden = true;
          if (this.states.success) this.states.success.hidden = false;
        }

        showError(message) {
          if (!this.errorEl) return;
          if (message) this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        }

        markSubscribed() {
          store.set(this.keys.subscribed, true, 400);
          store.remove(this.keys.dismissed);
          this.hideTab();
          if (!store.has(this.keys.barClosed)) this.showBar();
        }

        async handleSubmit(evt) {
          evt.preventDefault();
          if (this.hasAttribute('data-busy')) return;
          this.setAttribute('data-busy', '');
          if (this.errorEl) this.errorEl.hidden = true;

          const formData = new FormData(this.form);
          const email = (formData.get('contact[email]') || '').trim();
          if (!email) {
            this.removeAttribute('data-busy');
            return;
          }
          formData.set('contact[email]', email);
          store.set(this.keys.pending, true, 1);

          try {
            const response = await fetch(this.form.action, {
              method: 'POST',
              body: formData,
              credentials: 'same-origin',
              redirect: 'follow',
              headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            const finalUrl = response.url || '';

            if (response.ok && finalUrl.includes('customer_posted=true')) {
              store.remove(this.keys.pending);
              this.markSubscribed();
              this.showSuccess();
              return;
            }

            // Anything else (400 from hCaptcha/spam protection, a /challenge page,
            // or a re-rendered page with errors): hand over to a native submit so
            // Shopify's own captcha script can do its thing. init() picks the
            // result up from ?customer_posted=true after the redirect.
            this.form.submit();
          } catch (e) {
            store.remove(this.keys.pending);
            this.showError();
          } finally {
            this.removeAttribute('data-busy');
          }
        }
      }

      customElements.define('mable-popup', MablePopup);
    });
  }
})();
